import Dice from '@/models/Dice';
import Regions from '@/models/Regions';
import TerrainTypes from '@/models/TerrainTypes';
import Feature from '@/models/Feature';

export default class GameTable {
  constructor(region = Regions[0]) {
    this.region = region;
    this.hillsCount = 0;
    this.woodsCount = 0;
    this.marshCount = 0;
    this.habitationCount = 0;
    this.riverCrossing = 'Ford';
    this.features = [];
    this.widthInInches = 12 * 6;
    this.heightInInches = 12 * 4;
    this.crossingAngle = 0;
    this.isInitialized = false;
  }

  getRandomX(size, minimumFromCorner) {
    return minimumFromCorner + size * Math.floor(Math.random() * ((this.widthInInches - (minimumFromCorner * 2)) / size));
  }

  getRandomY(size, minimumFromCorner) {
    return minimumFromCorner + size * Math.floor(Math.random() * ((this.heightInInches - (minimumFromCorner * 2)) / size));
  }

  getFeaturesAtPosition(pos) {
    const x = Math.floor(pos.x / 12) * 12;
    const y = Math.floor(pos.y / 12) * 12;
    return this.features.filter(f => f.position.x >= x && (f.position.x) < (x + 12)
      && f.position.y >= y && f.position.y < (y + 12));
  }

  getNonRiverRandomPosition(size = 12, onEdge = null, minimumFromCorner = 0, allowedTypes = null) {
    let pos = this.getRandomPosition(size, onEdge, minimumFromCorner);
    const getFeatures = (pos, size) => {
      return this.getFeaturesAtPosition(pos, size).filter(f => f.terrainType === TerrainTypes.River || (allowedTypes != null && !allowedTypes.includes(f.terrainType)));
    };
    while (getFeatures(pos, size).length > 0) {
      pos = this.getRandomPosition(size, onEdge, minimumFromCorner);
    }
    return pos;
  }

  getEmptyRandomPosition(size = 12, onEdge = null, minimumFromCorner = 0) {
    let pos = this.getRandomPosition(size, onEdge, minimumFromCorner);
    while (this.getFeaturesAtPosition(pos, size).length > 0) {
      pos = this.getRandomPosition(size, onEdge, minimumFromCorner);
    }
    return pos;
  }

  getRandomPosition(size = 12, onEdge = null, minimumFromCorner = 0, centerFeature = true) {
    const padding = centerFeature ? (12 - size) / 2 : 0;
    if (onEdge != null) {
      const edge = onEdge[Math.floor(onEdge.length * Math.random())];
      if (edge.x == null) {
        return {
          x: this.getRandomX(size, minimumFromCorner),
          y: edge.y,
        };
      } else {
        return {
          x: edge.x,
          y: this.getRandomY(size, minimumFromCorner),
        };
      }
    } else {
      return {
        x: padding + this.getRandomX(12, minimumFromCorner),
        y: padding + this.getRandomY(12, minimumFromCorner),
      };
    }
  }

  addRandomFeature(terrainType, size = 12) {
    switch (terrainType) {
      case TerrainTypes.Habitation:
        this.features.push(new Feature(this.getNonRiverRandomPosition(size), size, terrainType));
        break;
      case TerrainTypes.Woods:
        this.features.push(new Feature(this.getNonRiverRandomPosition(size, null, 0, [TerrainTypes.Hills]), size, terrainType));
        break;
      default:
        this.features.push(new Feature(this.getEmptyRandomPosition(size), size, terrainType));
        break;
    }
  }

  combineFeatures(terrainType) {
    for (let x = 0; x < this.widthInInches; x += 24) {
      for (let y = 0; y < this.heightInInches; y += 24) {
        const matches = this.features.filter(f => f.position.x >= x
          && f.position.x < x + 24
          && f.position.y >= y
          && f.position.y < y + 24
          && f.terrainType === terrainType);
        if (matches.length === 3) {
          const positions = [{ x, y }, { x, y: y + 12 }, { x: x + 12, y }, { x: x + 12, y: y + 12 }];
          const positionToFill = positions.filter(p => !matches.map(f => f.position).some(p2 => p2.x === p.x && p2.y === p.y))[0];
          const existingFeature = this.features.find(f => f.position.x === positionToFill.x && f.position.y === positionToFill.y);
          if (existingFeature != null) {
            existingFeature.terrainType = terrainType;
          } else {
            this.features.push(new Feature(positionToFill, 12, terrainType));
          }
          // debugger; // eslint-disable-line
        }
      }
    }
  }

  generateRiver() {
    const edges = [{ x: 0 }, { x: this.widthInInches - 6 }, { y: 0 }, { y: this.heightInInches - 6 }];
    const startEdge = edges[Math.floor(Math.random() * edges.length)];
    this.riverStart = this.getRandomPosition(6, [startEdge], 18);
    this.riverEnd = this.getRandomPosition(6, edges.filter(x => x != startEdge), 18);
    this.features.push(new Feature(this.riverStart, 6, TerrainTypes.River));
    // this.features.push(new Feature(this.riverEnd, 6, TerrainTypes.River));
    let currentPos = this.riverStart;
    const xOffset = this.riverEnd.x > this.riverStart.x ? 6 : -6;
    const yOffset = this.riverEnd.y > this.riverStart.y ? 6 : -6;
    while (currentPos.x != this.riverEnd.x || currentPos.y != this.riverEnd.y) {
      currentPos = { x: currentPos.x, y: currentPos.y };
      if (currentPos.x != this.riverEnd.x && currentPos.y != this.riverEnd.y) {
        if (Math.floor(Math.random() * 2) === 0) {
          currentPos.x += xOffset;
        } else {
          currentPos.y += yOffset;
        }
      } else if (currentPos.x != this.riverEnd.x) {
        currentPos.x += xOffset;
      } else {
        currentPos.y += yOffset;
      }
      this.features.push(new Feature(currentPos, 6, TerrainTypes.River));
    }
    const centerIndex = Math.floor(this.features.length / 2);
    const center = this.features[centerIndex];
    this.crossingAngle = null;
    if (this.features[centerIndex - 1].position.x === this.features[centerIndex + 1].position.x) {
      this.crossingAngle = Math.PI / 2;
    } else if (this.features[centerIndex - 1].position.y === this.features[centerIndex + 1].position.y) {
      this.crossingAngle = 0;
    } else if (this.riverStart.x < this.riverEnd.x && this.riverStart.y < this.riverEnd.y) {
      this.crossingAngle = Math.PI / 4;
    } else if (this.riverStart.x < this.riverEnd.x && this.riverStart.y > this.riverEnd.y) {
      this.crossingAngle = 3 * Math.PI / 4;
    } else if (this.riverStart.x > this.riverEnd.x && this.riverStart.y < this.riverEnd.y) {
      this.crossingAngle = 3 * Math.PI / 4;
    } else if (this.riverStart.x > this.riverEnd.x && this.riverStart.y > this.riverEnd.y) {
      this.crossingAngle = Math.PI / 4;
    }
    this.features.push(new Feature({ ...center.position }, center.size, TerrainTypes.RiverCrossing));
  }

  get isRiverValid() {
    const riverFeatures = this.features.filter(x => x.terrainType === TerrainTypes.River);
    const west = riverFeatures.filter(f => f.position.x === 0);
    const north = riverFeatures.filter(f => f.position.y === 0);
    const east = riverFeatures.filter(f => f.position.x === this.widthInInches - 6);
    const south = riverFeatures.filter(f => f.position.y === this.heightInInches - 6);
    if (west.length > riverFeatures.length / 2
      || north.length > riverFeatures.length / 2
      || east.length > riverFeatures.length / 2
      || south.length > riverFeatures.length / 2) {
      return false;
    }
    return true;
  }

  generateTerrain() {
    const player1Roll = Dice.d6();
    const player2Roll = Dice.d6();
    this.hasRiver = player1Roll === player2Roll;
    if (this.hasRiver) {
      this.generateRiver();
      while (!this.isRiverValid) {
        this.features = [];
        this.generateRiver();
      }
    }

    this.hillsCount = this.region.terrain.find(x => x.type === TerrainTypes.Hills).value[Dice.d2() - 1];
    this.woodsCount = this.region.terrain.find(x => x.type === TerrainTypes.Woods).value[Dice.d2() - 1];
    this.marshCount = this.region.terrain.find(x => x.type === TerrainTypes.Marsh).value[Dice.d2() - 1];
    this.habitationCount = this.region.terrain.find(x => x.type === TerrainTypes.Habitation).value[Dice.d2() - 1];
    this.riverCrossing = this.region.terrain.find(x => x.type === TerrainTypes.RiverCrossing).value.length > 1
      ? this.region.terrain.find(x => x.type === TerrainTypes.RiverCrossing).value[Dice.d2() - 1]
      : this.region.terrain.find(x => x.type === TerrainTypes.RiverCrossing).value[0];

    for (let i = 0; i < this.hillsCount; i += 1) {
      this.addRandomFeature(TerrainTypes.Hills);
    }

    this.combineFeatures(TerrainTypes.Hills);

    for (let i = 0; i < this.woodsCount; i += 1) {
      this.addRandomFeature(TerrainTypes.Woods);
    }
    this.combineFeatures(TerrainTypes.Woods);

    for (let i = 0; i < this.marshCount; i += 1) {
      this.addRandomFeature(TerrainTypes.Marsh);
    }
    this.combineFeatures(TerrainTypes.Marsh);

    for (let i = 0; i < this.habitationCount; i += 1) {
      this.addRandomFeature(TerrainTypes.Habitation, 9);
    }

    this.isInitialized = true;
  }
}
